import { PublicError } from '#helper-components';
import { useTranslation } from '@modernloop/i18n';
import { Box, Button, Menu, MenuItem, TextField, Typography, alpha } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { TextFieldProps } from '@mui/material/TextField';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  COUNTRY_OPTIONS,
  CountryOption,
  DEFAULT_COUNTRY_CODE,
  buildCountryLocalization,
  getCountryOptionByCode,
  getFlagEmoji,
} from './country-data';

export interface CountryData {
  name: string;
  dialCode: string;
  countryCode: string;
}

interface Props {
  shouldValidate?: boolean;
  disabled?: boolean;
  setPhoneNumber: (phoneNumber: string) => void;
  setIsPhoneNumberValid?: (isValid: boolean) => void;
  onBlur?: () => void;
  value?: string;
  inputProps?: TextFieldProps['inputProps'];
  translate?: (key: string) => string;
}

const phoneUtil = PhoneNumberUtil.getInstance();

const detectCountryFromValue = (phoneValue: string | undefined): string | undefined => {
  if (!phoneValue) {
    return undefined;
  }

  try {
    const parsed = phoneUtil.parse(phoneValue);
    const region = phoneUtil.getRegionCodeForNumber(parsed);
    if (region) {
      return region.toLowerCase();
    }
  } catch {
    // Fall back to default country when the value cannot be parsed yet.
  }

  return undefined;
};

const formatPhoneNumber = (phoneValue: string, countryCode: string): string => {
  try {
    const parsed = phoneUtil.parseAndKeepRawInput(phoneValue, countryCode.toUpperCase());
    return phoneUtil.formatInOriginalFormat(parsed, countryCode.toUpperCase());
  } catch {
    return phoneValue;
  }
};

const validatePhoneNumber = (phoneValue: string, countryCode: string): boolean => {
  try {
    const defaultRegion = countryCode.toUpperCase();
    const parsed = phoneUtil.parse(phoneValue, defaultRegion);
    const parsedRegion = phoneUtil.getRegionCodeForNumber(parsed);
    const validationRegion = phoneValue.trim().startsWith('+') && parsedRegion ? parsedRegion : defaultRegion;
    return phoneUtil.isValidNumberForRegion(parsed, validationRegion);
  } catch {
    return false;
  }
};

/** MUI5 phone number input with country selector and libphonenumber validation. */
const PhoneNumberInput: React.FC<Props> = ({
  setPhoneNumber,
  setIsPhoneNumberValid,
  value,
  disabled = false,
  inputProps,
  shouldValidate = true,
  onBlur,
  translate,
}) => {
  const { t: defaultT, i18n } = useTranslation();
  const t = translate ?? defaultT;
  const [phoneNumberTouched, setPhoneNumberTouched] = useState(false);
  const [internalPhoneNumberValid, setInternalPhoneNumberValid] = useState(!shouldValidate);
  const [selectedCountryCode, setSelectedCountryCode] = useState(
    () => detectCountryFromValue(value) ?? DEFAULT_COUNTRY_CODE
  );
  const [internalPhoneNumber, setInternalPhoneNumber] = useState('');
  const [countryMenuAnchor, setCountryMenuAnchor] = useState<HTMLElement | null>(null);

  const localization = useMemo(() => buildCountryLocalization(i18n.language), [i18n.language]);
  const selectedCountry = getCountryOptionByCode(selectedCountryCode) ?? getCountryOptionByCode(DEFAULT_COUNTRY_CODE);
  const displayValue = value ?? internalPhoneNumber;
  const countryMenuOpen = Boolean(countryMenuAnchor);

  const getLocalizedCountryName = (country: CountryOption) => localization[country.name] ?? country.name;

  const updateValidation = useCallback(
    (phoneValue: string, countryCode: string) => {
      const phoneNumberValid = !shouldValidate || validatePhoneNumber(phoneValue, countryCode);
      setInternalPhoneNumberValid(phoneNumberValid);
      if (setIsPhoneNumberValid) {
        setIsPhoneNumberValid(phoneNumberValid);
      }
    },
    [setIsPhoneNumberValid, shouldValidate]
  );

  const emitPhoneNumberChange = (phoneValue: string, country: CountryOption) => {
    const detectedCountry = getCountryOptionByCode(detectCountryFromValue(phoneValue) ?? '') ?? country;
    if (detectedCountry.countryCode !== selectedCountryCode) {
      setSelectedCountryCode(detectedCountry.countryCode);
    }

    const formattedValue = formatPhoneNumber(phoneValue, detectedCountry.countryCode);
    setInternalPhoneNumber(formattedValue);
    setPhoneNumber(formattedValue);
    updateValidation(formattedValue, detectedCountry.countryCode);
  };

  const handleCountrySelect = (country: CountryOption) => {
    setSelectedCountryCode(country.countryCode);
    setCountryMenuAnchor(null);
    emitPhoneNumberChange(displayValue, country);
  };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedCountry) {
      return;
    }
    emitPhoneNumberChange(event.target.value, selectedCountry);
  };

  const handleBlur = () => {
    if (displayValue) {
      setPhoneNumberTouched(true);
    }
    if (onBlur) {
      onBlur();
    }
  };

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external controlled value.
  useEffect(() => {
    if (value === undefined) {
      return;
    }

    setInternalPhoneNumber(value);
    const detectedCountryCode = detectCountryFromValue(value);
    const nextCountryCode = detectedCountryCode ?? selectedCountryCode;
    if (detectedCountryCode && detectedCountryCode !== selectedCountryCode) {
      setSelectedCountryCode(detectedCountryCode);
    }
    updateValidation(value, nextCountryCode);
  }, [selectedCountryCode, updateValidation, value]);

  return (
    <>
      <Box
        // eslint-disable-next-line no-restricted-syntax -- Preserves the existing phone input chrome during the MUI5 migration.
        sx={{
          backgroundColor: (theme: Theme) => theme.palette.secondary.main,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          borderRadius: '4px',
          border: (theme: Theme) => {
            return !internalPhoneNumberValid && phoneNumberTouched
              ? `2px solid ${theme.palette.borderState.error}!important`
              : `1px solid ${alpha(theme.palette.text.primary, 0.15)}!important`;
          },
          '&:focus-within': {
            boxShadow: (theme: Theme) => `0 0 0 1px ${alpha(theme.palette.borderState.focus, 0.45)}`,
            border: (theme: Theme) => `1px solid ${alpha(theme.palette.borderState.focus, 0.45)}!important`,
          },
        }}
      >
        <Button
          aria-label={t('hiring-dashboard-common:common.location.phoneNumberLabel')}
          aria-expanded={countryMenuOpen}
          aria-haspopup="menu"
          disabled={disabled}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => setCountryMenuAnchor(event.currentTarget)}
          // eslint-disable-next-line no-restricted-syntax -- Preserves the existing flag-button styling during the MUI5 migration.
          sx={{
            minWidth: 46,
            width: 46,
            height: 24,
            marginLeft: '4px',
            borderRadius: '4px',
            color: 'inherit',
            '&:hover': {
              backgroundColor: (theme: Theme) => theme.palette.primary.tint,
            },
            '&::after': {
              content: '"\\25BE"',
              fontSize: '16px',
              marginLeft: '4px',
              color: (theme: Theme) => alpha(theme.palette.text.primary, 0.57),
            },
          }}
        >
          <span aria-hidden>{getFlagEmoji(selectedCountry?.countryCode ?? DEFAULT_COUNTRY_CODE)}</span>
        </Button>
        <Menu anchorEl={countryMenuAnchor} open={countryMenuOpen} onClose={() => setCountryMenuAnchor(null)}>
          {COUNTRY_OPTIONS.map((country) => (
            <MenuItem key={country.countryCode} onClick={() => handleCountrySelect(country)}>
              {/* eslint-disable-next-line no-restricted-syntax -- Local layout for country menu rows. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span aria-hidden>{getFlagEmoji(country.countryCode)}</span>
                <Typography variant="body2">{getLocalizedCountryName(country)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {country.dialCode}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Menu>
        <TextField
          value={displayValue}
          onChange={handlePhoneNumberChange}
          onBlur={handleBlur}
          disabled={disabled}
          inputProps={inputProps}
          variant="standard"
          fullWidth
          // eslint-disable-next-line no-restricted-syntax -- Preserves the existing underline removal during the MUI5 migration.
          sx={{
            '.MuiInput-underline:after, .MuiInput-underline:before, .MuiInput-underline:hover:not(.Mui-disabled):before':
              {
                borderBottom: '0px',
              },
          }}
        />
      </Box>
      {!internalPhoneNumberValid && phoneNumberTouched && (
        <PublicError error={t('hiring-dashboard-common:common.validation.invalidPhoneNumber')} />
      )}
    </>
  );
};

export default PhoneNumberInput;
